import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Target,
  ArrowRight,
  Calendar,
  CalendarRange,
  X,
  Building,
  CreditCard
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from "recharts";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { API } from "../utils/api";

const COLORS = ["#22c55e", "#06b6d4", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#6366f1"];
const ASSET_COLORS = {
  "Equity": "#22c55e",
  "Debt": "#06b6d4", 
  "Commodities": "#f59e0b",
  "Cash": "#8b5cf6",
  "Crypto": "#f97316",
  "Property": "#ec4899"
};

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Custom tooltip with better contrast
const CustomTooltip = ({ active, payload, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 shadow-lg">
        <p className="text-white font-medium">{payload[0].name || payload[0].payload?.name}</p>
        <p className="text-green-400 font-bold">
          {formatCurrency(payload[0].value, currency)}
        </p>
      </div>
    );
  }
  return null;
};

// Custom label to show percentage inside pie chart
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      className="text-xs font-bold"
      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Legend formatter function
const legendFormatter = (value) => <span className="text-gray-400 text-xs">{value}</span>;

export default function Dashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [monthlyBudget, setMonthlyBudget] = useState({ total_income: 0, total_expenses: 0, total_savings: 0, net: 0 });
  const [annualBudget, setAnnualBudget] = useState({ monthly_net_balance_annual: 0, annual_savings: 0 });
  const [futurePlans, setFuturePlans] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});
  const [latestBalances, setLatestBalances] = useState({});
  const [balanceMonth, setBalanceMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [ytdGrowth, setYtdGrowth] = useState(null);
  
  // Drill-down modal state
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState("");
  const [drillDownAccounts, setDrillDownAccounts] = useState([]);
  const [drillDownType, setDrillDownType] = useState(""); // "owner" or "asset"

  const navigate = useNavigate();
  const currency = user?.default_currency || "EUR";

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  // Refresh whenever the user returns to this tab or regains focus.
  // Keeps YTD / balance numbers in sync with edits made on other pages.
  useEffect(() => {
    const onFocus = () => fetchAllData();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchAllData();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    try {
      const [accountsRes, investmentsRes, monthlyRes, annualRes, plansRes, ratesRes, latestBalanceRes, ytdRes] = await Promise.all([
        axios.get(`${API}/accounts`),
        axios.get(`${API}/investments`),
        axios.get(`${API}/monthly-budget`),
        axios.get(`${API}/annual-budget`),
        axios.get(`${API}/future-plans`),
        axios.get(`${API}/exchange-rates?base=${currency}`),
        axios.get(`${API}/monthly-balances/latest`),
        axios.get(`${API}/dashboard/ytd-growth`)
      ]);
      
      setAccounts(accountsRes.data);
      setInvestments(investmentsRes.data);
      setMonthlyBudget(monthlyRes.data);
      setAnnualBudget(annualRes.data);
      setFuturePlans(plansRes.data);
      setExchangeRates(ratesRes.data.rates || {});
      setYtdGrowth(ytdRes.data || null);
      
      // Use latest monthly balances if available
      if (latestBalanceRes.data && latestBalanceRes.data.balances) {
        setLatestBalances(latestBalanceRes.data.balances);
        setBalanceMonth({ year: latestBalanceRes.data.year, month: latestBalanceRes.data.month });
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Convert amount to default currency
  const convertToDefaultCurrency = (amount, fromCurrency) => {
    if (fromCurrency === currency) return amount;
    const rate = exchangeRates[fromCurrency.toLowerCase()];
    if (rate && rate > 0) {
      return amount / rate;
    }
    return amount;
  };

  // Get account balance - use latest monthly snapshot if available, otherwise use account balance
  const getAccountBalance = (account) => {
    if (latestBalances[account.account_id] !== undefined) {
      return latestBalances[account.account_id];
    }
    return account.balance;
  };

  // Calculate total balance across all accounts using latest balances
  const totalBalance = accounts.reduce((sum, acc) => {
    return sum + convertToDefaultCurrency(getAccountBalance(acc), acc.currency || "EUR");
  }, 0);

  // Prepare data for balance by owner name (bar chart)
  const balanceByOwnerMap = {};
  accounts.forEach(acc => {
    const ownerName = acc.owner_name || acc.category || "Personal";
    const convertedBalance = convertToDefaultCurrency(getAccountBalance(acc), acc.currency || "EUR");
    balanceByOwnerMap[ownerName] = (balanceByOwnerMap[ownerName] || 0) + convertedBalance;
  });
  const balanceByOwnerData = Object.entries(balanceByOwnerMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Prepare data for asset allocation by owner (pie chart)
  // Shows how each owner's balance is divided across Cash, Equity, Debt, Commodities
  const ownerAssetAllocation = {};
  accounts.forEach(acc => {
    const ownerName = acc.owner_name || acc.category || "Personal";
    const investment = investments.find(inv => inv.account_id === acc.account_id);
    const convertedBalance = convertToDefaultCurrency(getAccountBalance(acc), acc.currency || "EUR");
    
    if (!ownerAssetAllocation[ownerName]) {
      ownerAssetAllocation[ownerName] = { Equity: 0, Debt: 0, Commodities: 0, Cash: 0, Crypto: 0, Property: 0 };
    }
    
    if (investment?.composition) {
      const comp = investment.composition;
      ownerAssetAllocation[ownerName].Equity += convertedBalance * (comp.equity_percent || 0) / 100;
      ownerAssetAllocation[ownerName].Debt += convertedBalance * (comp.debt_percent || 0) / 100;
      ownerAssetAllocation[ownerName].Commodities += convertedBalance * (comp.commodities_percent || 0) / 100;
      ownerAssetAllocation[ownerName].Cash += convertedBalance * (comp.cash_percent || 0) / 100;
      ownerAssetAllocation[ownerName].Crypto += convertedBalance * (comp.crypto_percent || 0) / 100;
      ownerAssetAllocation[ownerName].Property += convertedBalance * (comp.property_percent || 0) / 100;
    } else {
      // No investment composition, treat as cash
      ownerAssetAllocation[ownerName].Cash += convertedBalance;
    }
  });

  // Flatten to pie chart data: "Owner - Asset Class"
  const assetAllocationData = [];
  Object.entries(ownerAssetAllocation).forEach(([owner, assets]) => {
    if (assets.Equity > 0) {
      assetAllocationData.push({ name: `${owner} - Equity`, value: assets.Equity, asset: "Equity", owner });
    }
    if (assets.Debt > 0) {
      assetAllocationData.push({ name: `${owner} - Debt`, value: assets.Debt, asset: "Debt", owner });
    }
    if (assets.Commodities > 0) {
      assetAllocationData.push({ name: `${owner} - Commodities`, value: assets.Commodities, asset: "Commodities", owner });
    }
    if (assets.Cash > 0) {
      assetAllocationData.push({ name: `${owner} - Cash`, value: assets.Cash, asset: "Cash", owner });
    }
    if (assets.Crypto > 0) {
      assetAllocationData.push({ name: `${owner} - Crypto`, value: assets.Crypto, asset: "Crypto", owner });
    }
    if (assets.Property > 0) {
      assetAllocationData.push({ name: `${owner} - Property`, value: assets.Property, asset: "Property", owner });
    }
  });

  // Sort and filter small values
  const filteredAssetData = assetAllocationData
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value);

  // Click handler for Balance by Owner bar chart
  const handleOwnerBarClick = (data) => {
    if (!data || !data.name) return;
    const ownerName = data.name;
    
    // Find all accounts for this owner
    const ownerAccounts = accounts.filter(acc => {
      const accOwner = acc.owner_name || acc.category || "Personal";
      return accOwner === ownerName;
    }).map(acc => ({
      ...acc,
      displayBalance: getAccountBalance(acc),
      convertedBalance: convertToDefaultCurrency(getAccountBalance(acc), acc.currency || "EUR")
    }));
    
    setDrillDownTitle(`${ownerName}'s Accounts`);
    setDrillDownAccounts(ownerAccounts);
    setDrillDownType("owner");
    setDrillDownOpen(true);
  };

  // Click handler for Asset Allocation pie chart
  const handleAssetPieClick = (data, owner) => {
    if (!data || !data.name) return;
    const assetClass = data.name; // "Equity", "Debt", "Commodities", "Cash"
    
    // Find all accounts for this owner that contribute to this asset class
    const relevantAccounts = accounts.filter(acc => {
      const accOwner = acc.owner_name || acc.category || "Personal";
      if (accOwner !== owner) return false;
      
      const investment = investments.find(inv => inv.account_id === acc.account_id);
      
      if (!investment?.composition) {
        // No investment composition means it's cash
        return assetClass === "Cash";
      }
      
      // Check if this account has allocation in the clicked asset class
      const comp = investment.composition;
      switch (assetClass) {
        case "Equity": return (comp.equity_percent || 0) > 0;
        case "Debt": return (comp.debt_percent || 0) > 0;
        case "Commodities": return (comp.commodities_percent || 0) > 0;
        case "Cash": return (comp.cash_percent || 0) > 0;
        case "Crypto": return (comp.crypto_percent || 0) > 0;
        case "Property": return (comp.property_percent || 0) > 0;
        default: return false;
      }
    }).map(acc => {
      const investment = investments.find(inv => inv.account_id === acc.account_id);
      const balance = getAccountBalance(acc);
      const convertedBalance = convertToDefaultCurrency(balance, acc.currency || "EUR");
      
      let assetContribution = 0;
      let assetPercent = 0;
      
      if (!investment?.composition) {
        if (assetClass === "Cash") {
          assetContribution = convertedBalance;
          assetPercent = 100;
        }
      } else {
        const comp = investment.composition;
        switch (assetClass) {
          case "Equity": 
            assetPercent = comp.equity_percent || 0;
            break;
          case "Debt": 
            assetPercent = comp.debt_percent || 0;
            break;
          case "Commodities": 
            assetPercent = comp.commodities_percent || 0;
            break;
          case "Cash": 
            assetPercent = comp.cash_percent || 0;
            break;
          case "Crypto":
            assetPercent = comp.crypto_percent || 0;
            break;
          case "Property":
            assetPercent = comp.property_percent || 0;
            break;
        }
        assetContribution = convertedBalance * assetPercent / 100;
      }
      
      return {
        ...acc,
        displayBalance: balance,
        convertedBalance,
        assetContribution,
        assetPercent
      };
    });
    
    setDrillDownTitle(`${owner} - ${assetClass} Allocation`);
    setDrillDownAccounts(relevantAccounts);
    setDrillDownType("asset");
    setDrillDownOpen(true);
  };

  // Get icon for account type
  const getAccountIcon = (type) => {
    switch (type) {
      case "investment": return TrendingUp;
      case "checking": return Building;
      case "credit_card": return CreditCard;
      default: return Wallet;
    }
  };

  if (loading) {
    return (
      <Layout user={user} pageTitle="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Dashboard">
      <div data-testid="dashboard-page" className="space-y-8">
        {/* Page Header */}
        <PageHeader pageKey="dashboard" />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Balance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-green-500/20 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Balance</p>
                    <p className="text-2xl font-bold text-green-400 number-display">
                      {formatCurrency(totalBalance, currency)}
                    </p>
                    <p className="text-xs text-gray-500">{accounts.length} accounts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Monthly Net Balance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/monthly">
              <Card className="bg-card border-border hover:border-blue-500/30 transition-colors cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Monthly Net Balance</p>
                      <p className={`text-2xl font-bold number-display ${monthlyBudget.net >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                        {formatCurrency(monthlyBudget.net, currency)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Savings: {formatCurrency(monthlyBudget.total_savings, currency)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>

          {/* Annual Savings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Link to="/annual">
              <Card className="bg-card border-border hover:border-purple-500/30 transition-colors cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                      <CalendarRange className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Annual Savings</p>
                      <p className={`text-2xl font-bold number-display ${annualBudget.annual_savings >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                        {formatCurrency(annualBudget.annual_savings, currency)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Net × 12: {formatCurrency(annualBudget.monthly_net_balance_annual, currency)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>

        </div>

        {/* Balance by Owner Bar Chart - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-bold text-white flex items-center justify-between">
                Balance by Owner
                <Link to="/accounts">
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardTitle>
              {ytdGrowth && ytdGrowth.total && (
                <div
                  className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1"
                  data-testid="ytd-growth-total"
                >
                  <span className="text-xs text-gray-500 uppercase tracking-wider">
                    YTD Growth ({ytdGrowth.year})
                  </span>
                  <span
                    className={`text-lg font-bold number-display ${
                      ytdGrowth.total.delta >= 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {ytdGrowth.total.delta >= 0 ? "+" : ""}
                    {formatCurrency(ytdGrowth.total.delta, currency)}
                  </span>
                  {ytdGrowth.total.delta_percent !== null && (
                    <span
                      className={`text-xs font-semibold ${
                        ytdGrowth.total.delta >= 0 ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      ({ytdGrowth.total.delta_percent >= 0 ? "+" : ""}
                      {ytdGrowth.total.delta_percent}%)
                    </span>
                  )}
                  {ytdGrowth.baseline_month && (
                    <span className="text-[10px] text-gray-500">
                      since{" "}
                      {new Date(
                        ytdGrowth.baseline_year,
                        ytdGrowth.baseline_month - 1,
                        1
                      ).toLocaleString("en", { month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {balanceByOwnerData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={balanceByOwnerData} layout="vertical" margin={{ left: 20 }}>
                      <XAxis 
                        type="number" 
                        tickFormatter={(value) => formatCurrency(value, currency)} 
                        stroke="#71717a"
                        fontSize={10}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={120} 
                        stroke="#71717a"
                        fontSize={11}
                        tick={{ fill: '#a1a1aa' }}
                      />
                      <Tooltip content={<CustomTooltip currency={currency} />} />
                      <Bar 
                        dataKey="value" 
                        fill="#22c55e" 
                        radius={[0, 4, 4, 0]}
                        onClick={handleOwnerBarClick}
                        style={{ cursor: 'pointer' }}
                      >
                        {balanceByOwnerData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-gray-500 text-center mt-2">Click on a bar to see accounts</p>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <div className="text-center">
                    <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500">No accounts yet</p>
                    <Link to="/accounts">
                      <Button className="mt-3 bg-green-500 hover:bg-green-400 text-black rounded-full text-sm">
                        Add Account
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {ytdGrowth && ytdGrowth.by_owner && Object.keys(ytdGrowth.by_owner).length > 0 && (
                <div
                  className="mt-4 pt-4 border-t border-border"
                  data-testid="ytd-growth-by-owner"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">
                    Year-to-date growth per owner
                  </p>
                  <div className="space-y-2">
                    {Object.entries(ytdGrowth.by_owner)
                      .sort((a, b) => b[1].current - a[1].current)
                      .map(([owner, data]) => (
                        <div
                          key={owner}
                          className="flex items-center justify-between gap-3 p-2 bg-secondary/40 rounded-lg"
                          data-testid={`ytd-owner-${owner}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate">{owner}</p>
                            <p className="text-[10px] text-gray-500">
                              {formatCurrency(data.start, currency)} → {formatCurrency(data.current, currency)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p
                              className={`text-sm font-bold number-display ${
                                data.delta >= 0 ? "text-green-400" : "text-red-400"
                              }`}
                            >
                              {data.delta >= 0 ? "+" : ""}
                              {formatCurrency(data.delta, currency)}
                            </p>
                            {data.delta_percent !== null && (
                              <p
                                className={`text-[10px] ${
                                  data.delta >= 0 ? "text-green-400" : "text-red-400"
                                }`}
                              >
                                {data.delta_percent >= 0 ? "+" : ""}
                                {data.delta_percent}%
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Asset Allocation Pie Charts - One per Owner */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

          {/* Asset Allocation by Owner - Separate Pie Charts */}
          {Object.keys(ownerAssetAllocation).length > 0 ? (
            Object.entries(ownerAssetAllocation).map(([owner, assets], ownerIndex) => {
              const ownerPieData = [
                { name: "Equity", value: assets.Equity },
                { name: "Debt", value: assets.Debt },
                { name: "Commodities", value: assets.Commodities },
                { name: "Cash", value: assets.Cash },
                { name: "Crypto", value: assets.Crypto },
                { name: "Property", value: assets.Property },
              ].filter(item => item.value > 0);
              
              const ownerTotal = balanceByOwnerMap[owner] || 0;
              
              if (ownerPieData.length === 0) return null;
              
              return (
                <motion.div
                  key={owner}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + ownerIndex * 0.1 }}
                >
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-bold text-white flex items-center justify-between">
                        <div>
                          <span>{owner}</span>
                          <span className="text-sm font-normal text-gray-400 ml-2">
                            ({formatCurrency(ownerTotal, currency)})
                          </span>
                        </div>
                        <Link to="/investments">
                          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={ownerPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              paddingAngle={2}
                              dataKey="value"
                              nameKey="name"
                              label={renderCustomLabel}
                              labelLine={false}
                              onClick={(data) => handleAssetPieClick(data, owner)}
                              style={{ cursor: 'pointer' }}
                            >
                              {ownerPieData.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={ASSET_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                                />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip currency={currency} />} />
                            <Legend 
                              wrapperStyle={{ color: "#a1a1aa", fontSize: "10px" }}
                              formatter={legendFormatter}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <p className="text-xs text-gray-500 text-center">Click on a segment to see accounts</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-bold text-white">
                    Asset Allocation by Owner
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-56 flex items-center justify-center">
                    <div className="text-center">
                      <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-500">No investment data</p>
                      <Link to="/investments">
                        <Button className="mt-3 bg-green-500 hover:bg-green-400 text-black rounded-full text-sm">
                          Set Allocations
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Future Plans Preview */}
        {futurePlans.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold text-white flex items-center justify-between">
                  Your Future Plans
                  <Link to="/planning">
                    <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                      View All <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {futurePlans.slice(0, 3).map((plan) => (
                    <Link key={plan.plan_id} to="/planning">
                      <div className="p-4 bg-secondary rounded-xl hover:bg-secondary/80 transition-colors">
                        <h3 className="font-bold text-white mb-1">{plan.name}</h3>
                        <p className="text-sm text-gray-500 mb-2">
                          {plan.start_year} - {plan.start_year + plan.years_to_forecast - 1}
                        </p>
                        <p className="text-lg font-bold text-green-400 number-display">
                          {formatCurrency(plan.forecast?.[plan.forecast.length - 1]?.ending_balance || 0, currency)}
                        </p>
                        <p className="text-xs text-gray-500">Projected final balance</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Drill-down Modal */}
        <Dialog open={drillDownOpen} onOpenChange={setDrillDownOpen}>
          <DialogContent className="bg-card border-border sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white text-xl">{drillDownTitle}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4">
              {drillDownAccounts.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No accounts found</p>
              ) : (
                drillDownAccounts.map((acc) => {
                  const Icon = getAccountIcon(acc.account_type);
                  return (
                    <div 
                      key={acc.account_id}
                      className="p-4 bg-secondary rounded-xl hover:bg-secondary/80 transition-colors cursor-pointer"
                      onClick={() => {
                        setDrillDownOpen(false);
                        navigate('/accounts');
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            acc.account_type === 'investment' ? 'bg-purple-500/10' :
                            acc.account_type === 'checking' ? 'bg-blue-500/10' : 
                            acc.account_type === 'credit_card' ? 'bg-red-500/10' : 'bg-green-500/10'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              acc.account_type === 'investment' ? 'text-purple-400' :
                              acc.account_type === 'checking' ? 'text-blue-400' : 
                              acc.account_type === 'credit_card' ? 'text-red-400' : 'text-green-400'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-white">{acc.name}</p>
                            <p className="text-xs text-gray-500 capitalize">{acc.account_type?.replace('_', ' ')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-400 number-display">
                            {formatCurrency(acc.convertedBalance, currency)}
                          </p>
                          {drillDownType === "asset" && acc.assetPercent !== undefined && (
                            <p className="text-xs text-gray-500">
                              {acc.assetPercent}% → {formatCurrency(acc.assetContribution, currency)}
                            </p>
                          )}
                          {acc.currency !== currency && (
                            <p className="text-xs text-gray-500">
                              ({formatCurrency(acc.displayBalance, acc.currency)})
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              
              {/* Total */}
              {drillDownAccounts.length > 0 && (
                <div className="pt-3 border-t border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Total</span>
                    <span className="text-xl font-bold text-green-400 number-display">
                      {formatCurrency(
                        drillDownType === "asset" 
                          ? drillDownAccounts.reduce((sum, acc) => sum + (acc.assetContribution || 0), 0)
                          : drillDownAccounts.reduce((sum, acc) => sum + acc.convertedBalance, 0),
                        currency
                      )}
                    </span>
                  </div>
                </div>
              )}
              
              <Button
                onClick={() => {
                  setDrillDownOpen(false);
                  navigate('/accounts');
                }}
                className="w-full bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold mt-4"
              >
                View All Accounts
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
